<script setup>
/**
 * SagaWorld — the engraved Norse atlas the /philosophy page travels
 * through. One continuous SVG landscape, authored in the site's
 * line-art dialect (stroke-only gold/violet/magenta on deep night,
 * deterministic PRNG for organic growth — see YggdrasilTree).
 *
 * The component is intentionally dumb: it only authors geometry and
 * ids/classes/data-attributes. The parent page owns the master GSAP
 * timeline and drives every act through those hooks:
 *
 *   #ridge-far / #ridge-mid / #cliff-left / #cliff-right / #bridge /
 *   #tree / #summit / #fg        — parallax layers (the "camera")
 *   .stone[data-*]               — the bridge, stone by stone
 *   #myth-arc*, #deck*, #rays,
 *   #threads, .t-draw            — pathLength=1 dash draw-ins
 *   #stars #aurora #eye-core ... — opacity gates
 *
 * Authored state = the cold prologue (broken bridge, hidden light) so
 * the SSG first paint is already chapter II's silent world.
 */

// --- deterministic PRNG (house idiom — same world on every load)
function mulberry32(seed) {
  return function () {
    seed |= 0
    seed = (seed + 0x6d2b79f5) | 0
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}
const rand = mulberry32(23)
const r1 = (n) => Math.round(n * 10) / 10
const rad = (d) => (d * Math.PI) / 180

// ================= the broken bridge =================
// Voussoirs authored along their FINAL arch; the broken pose lives in
// the transform attribute (+ data) so the prologue paints shattered.
const ARCH = { x0: 604, y0: 458, cx: 720, cy: 392, x1: 836, y1: 458 }
const archPoint = (t) => {
  const u = 1 - t
  return [
    u * u * ARCH.x0 + 2 * u * t * ARCH.cx + t * t * ARCH.x1,
    u * u * ARCH.y0 + 2 * u * t * ARCH.cy + t * t * ARCH.y1
  ]
}
// [broken dx, dy, rot] and [fracture extra dx, dy, rot] per stone —
// the middle of the span is gone (levitating in the abyss), the
// shoulders sag, the abutments barely hold.
const STONE_POSES = [
  { b: [0, 2, -2], f: [-3, 8, -5] },
  { b: [-2, 5, 3], f: [4, 12, 6] },
  { b: [-9, 26, -22], f: [-16, 96, -44] },
  { b: [12, 172, -62], f: [-28, 58, -30], float: true },
  { b: [-7, 198, 47], f: [8, 72, 34], float: true },
  { b: [11, 150, 76], f: [26, 56, 42], float: true },
  { b: [7, 32, 19], f: [12, 108, 40] },
  { b: [2, 7, -4], f: [-4, 14, -7] },
  { b: [0, 3, 2], f: [3, 9, 5] }
]
const stones = STONE_POSES.map((pose, i) => {
  const t = (i + 0.5) / STONE_POSES.length
  const [px, py] = archPoint(t)
  const [nx2, ny2] = archPoint(Math.min(t + 0.01, 1))
  const a = Math.atan2(ny2 - py, nx2 - px)
  const ux = Math.cos(a), uy = Math.sin(a)
  const vx = -uy, vy = ux
  const hl = 12.6, hw = 6
  const c = (sx, sy) => `${r1(px + ux * sx + vx * sy)} ${r1(py + uy * sx + vy * sy)}`
  const jb = pose.b.map((v) => v + (rand() - 0.5) * (pose.float ? 14 : 3))
  return {
    d: `M${c(-hl, -hw)} L${c(hl, -hw)} L${c(hl + 1.5, hw)} L${c(-hl - 1.5, hw)} Z`,
    cx: r1(px),
    cy: r1(py),
    transform: `translate(${r1(jb[0])} ${r1(jb[1])}) rotate(${r1(jb[2])} ${r1(px)} ${r1(py)})`,
    fx: r1(pose.f[0]),
    fy: r1(pose.f[1]),
    fr: r1(pose.f[2]),
    float: pose.float || false
  }
})

// small rock shards adrift in the canyon, siblings of the lost stones
const shards = Array.from({ length: 5 }, (_, i) => {
  const cx = 668 + rand() * 120
  const cy = 520 + i * 42 + rand() * 22
  const s = 3.5 + rand() * 4
  const a0 = rand() * Math.PI
  const pts = [0, 1, 2].map((k) => {
    const a = a0 + k * 2.1 + rand() * 0.5
    return `${r1(cx + Math.cos(a) * s)} ${r1(cy + Math.sin(a) * s * 0.8)}`
  })
  return { d: `M${pts[0]} L${pts[1]} L${pts[2]} Z`, dx: r1((cx - 728) * 0.4), dy: r1(20 + rand() * 30) }
})

// ================= the world-tree =================
// Grown behind the left mountain with the Yggdrasil algorithm, scaled
// to the saga: every branch is a .t-draw stroke, every leaf a .t-leaf.
function qPoint(x0, y0, cx, cy, x1, y1, t) {
  const u = 1 - t
  return [u * u * x0 + 2 * u * t * cx + t * t * x1, u * u * y0 + 2 * u * t * cy + t * t * y1]
}
const tree = { paths: [], leaves: [] }
function grow(x, y, ang, len, w, depth) {
  const bend = (rand() - 0.5) * 30 + 7
  const endAng = ang + bend
  const cx = x + Math.cos(rad(ang)) * len * 0.55
  const cy = y - Math.sin(rad(ang)) * len * 0.55
  const ex = x + Math.cos(rad(endAng)) * len
  const ey = y - Math.sin(rad(endAng)) * len
  tree.paths.push({
    d: `M${r1(x)} ${r1(y)} Q${r1(cx)} ${r1(cy)} ${r1(ex)} ${r1(ey)}`,
    w: r1(Math.max(w, 0.7))
  })
  if (depth >= 2) {
    const n = 2 + Math.floor(rand() * 2)
    for (let k = 0; k < n; k++) {
      const dir = endAng - 50 + k * (100 / Math.max(n - 1, 1))
      tree.leaves.push(
        `translate(${r1(ex)} ${r1(ey)}) rotate(${r1(-dir + (rand() - 0.5) * 30)}) scale(${r1(0.5 + rand() * 0.3)})`
      )
    }
    return
  }
  const n = 2 + (rand() > 0.6 ? 1 : 0)
  for (let k = 0; k < n; k++) {
    const t = 0.5 + (k / n) * 0.45
    const [bx, by] = qPoint(x, y, cx, cy, ex, ey, t)
    grow(bx, by, endAng + (k - (n - 1) / 2) * (32 + rand() * 12), len * (0.6 + rand() * 0.16), w * 0.6, depth + 1)
  }
}
;[
  { ang: 162, len: 88 },
  { ang: 122, len: 82 },
  { ang: 92, len: 86 },
  { ang: 58, len: 78 },
  { ang: 22, len: 66 }
].forEach(({ ang, len }, i) => grow(347, 258 - i * 4, ang, len, 1.8, 0))

// ================= the sky =================
const stars = Array.from({ length: 84 }, (_, i) => ({
  x: r1(rand() * 1440),
  y: r1(rand() * 360),
  r: r1(0.5 + rand() * 1.1),
  o: r1(0.3 + rand() * 0.6),
  slow: i % 2 === 0
}))

// ================= the observatory ring =================
const OBS = { x: 1095, y: 120 }
const obsTicks = Array.from({ length: 24 }, (_, i) => {
  const a = (i * 15 * Math.PI) / 180
  const long = i % 6 === 0
  return {
    x1: r1(OBS.x + Math.cos(a) * (long ? 66 : 71)),
    y1: r1(OBS.y + Math.sin(a) * (long ? 66 : 71)),
    x2: r1(OBS.x + Math.cos(a) * 79),
    y2: r1(OBS.y + Math.sin(a) * 79),
    long
  }
})

// rays of understanding — the first three carry travelling pulses
const RAYS = [
  [700, 330], [540, 520], [860, 612], [1290, 432], [962, 660],
  [420, 312], [1242, 648], [806, 214]
].map(([x, y], i) => ({
  id: i < 3 ? `ray-${i}` : null,
  d: `M${OBS.x} ${OBS.y} Q${r1((OBS.x + x) / 2 + 24)} ${r1((OBS.y + y) / 2 - 30)} ${x} ${y}`
}))

// threads of the final composition — the whole journey, connected
const THREADS = [
  { id: 'thread-0', d: `M1095 120 C 980 300, 850 500, 720 636` }, // → the bridge
  { id: 'thread-1', d: `M1095 120 C 880 70, 560 180, 352 398` }, // → the tree
  { id: 'thread-2', d: `M1095 120 C 1090 280, 1050 460, 1022 580` }, // → the village
  { id: null, d: `M1095 120 C 800 180, 460 420, 246 572` }, // → the far lights
  { id: null, d: `M1095 120 C 940 340, 740 560, 608 682` } // → the left lantern
]
</script>

<template>
  <svg
    viewBox="0 0 1440 900"
    preserveAspectRatio="xMidYMid slice"
    fill="none"
    aria-hidden="true"
    class="saga w-full h-full"
  >
    <defs>
      <linearGradient id="saga-rainbow" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stop-color="#8b6ff0" stop-opacity="0" />
        <stop offset="0.22" stop-color="#8b6ff0" />
        <stop offset="0.5" stop-color="#d75fe8" />
        <stop offset="0.74" stop-color="#f0a75c" />
        <stop offset="0.92" stop-color="#ffd9a2" />
        <stop offset="1" stop-color="#ffd9a2" stop-opacity="0" />
      </linearGradient>
      <radialGradient id="saga-dawn" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0" stop-color="#f0a75c" stop-opacity="0.5" />
        <stop offset="0.45" stop-color="#d75fe8" stop-opacity="0.16" />
        <stop offset="1" stop-color="#f0a75c" stop-opacity="0" />
      </radialGradient>
      <radialGradient id="saga-halo" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0" stop-color="#ffe7bd" stop-opacity="0.9" />
        <stop offset="0.4" stop-color="#f0a75c" stop-opacity="0.35" />
        <stop offset="1" stop-color="#f0a75c" stop-opacity="0" />
      </radialGradient>
      <radialGradient id="saga-cloud" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0" stop-color="#cbbcff" stop-opacity="0.32" />
        <stop offset="1" stop-color="#cbbcff" stop-opacity="0" />
      </radialGradient>
      <radialGradient id="saga-abyss" cx="0.5" cy="0.9" r="0.72">
        <stop offset="0" stop-color="#030210" stop-opacity="0.85" />
        <stop offset="0.55" stop-color="#030210" stop-opacity="0.4" />
        <stop offset="1" stop-color="#030210" stop-opacity="0" />
      </radialGradient>
      <filter id="saga-glow" x="-40%" y="-40%" width="180%" height="180%">
        <feGaussianBlur stdDeviation="7" />
      </filter>
      <!-- almond leaf, house idiom -->
      <path id="saga-leaf" d="M0 0 C5 -9 13 -14 22 -15 C20 -6 12 -1 0 0 Z" />
    </defs>

    <g id="world">
      <!-- ===================== SKY ===================== -->
      <g id="sky-art">
        <g id="stars" opacity="0">
          <circle
            v-for="(s, i) in stars"
            :key="i"
            :cx="s.x"
            :cy="s.y"
            :r="s.r"
            fill="#d9c9ff"
            :opacity="s.o"
            :class="s.slow ? 'tw-a' : 'tw-b'"
          />
          <g id="constellations" stroke="#b7a3f6" stroke-width="0.6" opacity="0.55">
            <path
              class="t-draw"
              d="M180 96 L272 132 L344 88 L432 150 L508 118"
              pathLength="1"
              stroke-dasharray="1"
              stroke-dashoffset="1"
            />
            <path
              class="t-draw"
              d="M1210 60 L1268 108 L1342 84 L1396 148"
              pathLength="1"
              stroke-dasharray="1"
              stroke-dashoffset="1"
            />
          </g>
        </g>

        <g id="aurora" opacity="0">
          <g class="aurora-drift">
            <path d="M-80 150 C 300 60, 620 210, 940 120 C 1160 60, 1330 110, 1520 70" stroke="url(#saga-rainbow)" stroke-width="34" opacity="0.5" />
            <path d="M-60 220 C 340 140, 700 260, 1060 170 C 1260 120, 1400 160, 1520 140" stroke="#8b6ff0" stroke-width="22" opacity="0.4" />
          </g>
          <g class="aurora-drift-b">
            <path d="M-80 90 C 380 30, 760 150, 1180 60 C 1320 32, 1420 60, 1520 40" stroke="#d75fe8" stroke-width="14" opacity="0.35" />
          </g>
        </g>

        <!-- chapter I : the mythic bridge, a memory in the sky -->
        <g id="myth" opacity="0">
          <path
            id="myth-arc-glow"
            d="M240 430 C 520 140, 920 140, 1200 430"
            stroke="url(#saga-rainbow)"
            stroke-width="16"
            stroke-linecap="round"
            filter="url(#saga-glow)"
            opacity="0.55"
            pathLength="1"
            stroke-dasharray="1"
            stroke-dashoffset="1"
          />
          <path
            id="myth-arc"
            d="M240 430 C 520 140, 920 140, 1200 430"
            stroke="url(#saga-rainbow)"
            stroke-width="2.4"
            stroke-linecap="round"
            pathLength="1"
            stroke-dasharray="1"
            stroke-dashoffset="1"
          />
        </g>
      </g>

      <!-- warmth rising behind the far ridge (the healing begins) -->
      <circle id="dawn-glow" cx="1020" cy="560" r="330" fill="url(#saga-dawn)" opacity="0" />

      <!-- ===================== FAR RIDGE (hazy, the lightest mass) ===================== -->
      <g id="ridge-far">
        <path
          d="M0 560 L60 540 L140 500 L210 528 L300 470 L390 520 L470 492 L560 540 L640 500 L720 536 L810 486 L900 524 L980 476 L1080 520 L1160 488 L1250 530 L1330 502 L1440 544 L1440 900 L0 900 Z"
          fill="#181944"
        />
        <path
          d="M0 560 L60 540 L140 500 L210 528 L300 470 L390 520 L470 492 L560 540 L640 500 L720 536 L810 486 L900 524 L980 476 L1080 520 L1160 488 L1250 530 L1330 502 L1440 544"
          stroke="#565f8a"
          stroke-width="1"
          opacity="0.6"
        />
        <!-- lights far away, unable to reach each other -->
        <g id="far-lights" opacity="0" fill="#ffd9a2">
          <circle cx="240" cy="505" r="2" class="blink-a" />
          <circle cx="330" cy="516" r="1.7" class="blink-b" />
          <circle cx="176" cy="536" r="1.5" class="blink-c" />
        </g>
      </g>

      <!-- ===================== MID RIDGE ===================== -->
      <g id="ridge-mid">
        <path
          d="M0 660 L90 600 L180 632 L260 566 L350 618 L430 580 L520 634 L600 596 L700 648 L780 600 L870 650 L950 590 L1040 640 L1130 576 L1220 630 L1300 592 L1440 656 L1440 900 L0 900 Z"
          fill="#13103a"
        />
        <path
          d="M0 660 L90 600 L180 632 L260 566 L350 618 L430 580 L520 634 L600 596 L700 648 L780 600 L870 650 L950 590 L1040 640 L1130 576 L1220 630 L1300 592 L1440 656"
          stroke="#4a5578"
          stroke-width="1"
          opacity="0.75"
        />
      </g>

      <!-- ===================== SUMMIT (enters from above during the climb) ===================== -->
      <g id="summit" transform="translate(0 -980)">
        <!-- clouds passing below the peak -->
        <g id="clouds" opacity="0">
          <ellipse class="cloud-a" cx="420" cy="700" rx="180" ry="30" fill="url(#saga-cloud)" />
          <ellipse class="cloud-b" cx="880" cy="770" rx="230" ry="36" fill="url(#saga-cloud)" />
          <ellipse class="cloud-c" cx="1240" cy="680" rx="150" ry="26" fill="url(#saga-cloud)" />
        </g>

        <path
          d="M840 900 L862 770 L848 726 L888 596 L876 552 L916 430 L948 322 L978 240 L1002 150 L1005 200 L1185 200 L1192 128 L1216 170 L1246 260 L1288 372 L1320 470 L1352 580 L1338 620 L1368 730 L1392 830 L1400 900 Z"
          fill="#16123f"
        />
        <path
          d="M840 900 L862 770 L848 726 L888 596 L876 552 L916 430 L948 322 L978 240 L1002 150"
          stroke="#5b66a8"
          stroke-width="1.1"
          opacity="0.8"
        />
        <path
          d="M1192 128 L1216 170 L1246 260 L1288 372 L1320 470 L1352 580 L1338 620 L1368 730 L1392 830 L1400 900"
          stroke="#5b66a8"
          stroke-width="1.1"
          opacity="0.8"
        />
        <!-- rockface hatching -->
        <g stroke="#5b66a8" stroke-width="0.7" opacity="0.18">
          <path d="M968 300 L934 396" />
          <path d="M996 226 L964 322" />
          <path d="M934 452 L906 546" />
          <path d="M902 590 L878 682" />
          <path d="M1236 300 L1262 392" />
          <path d="M1282 428 L1306 520" />
          <path d="M1318 570 L1340 662" />
          <path d="M876 726 L858 812" />
        </g>

        <!-- the observatory : an ancient terrace and a great runic ring -->
        <g id="observatory">
          <path d="M1000 200 Q1095 176 1190 200" stroke="#8b6ff0" stroke-width="1.1" opacity="0.8" />
          <path d="M1014 212 Q1095 190 1176 212" stroke="#8b6ff0" stroke-width="0.7" opacity="0.5" />
          <!-- flanking stones, rune-marked -->
          <g stroke="#9d86ea" stroke-width="1.1" opacity="0.8">
            <path d="M990 240 L986 178 L996 166 L1004 180 L1002 240" />
            <path d="M992 198 L1002 206 M992 216 L1002 208" stroke-width="0.8" />
            <path d="M1206 252 L1202 200 L1212 190 L1220 202 L1218 252" />
            <path d="M1208 216 L1217 224 M1208 232 L1217 224" stroke-width="0.8" />
          </g>

          <!-- outer graduated ring — rotates as Heimdall reads the world -->
          <g id="ring-outer" class="obs-ring">
            <circle :cx="OBS.x" :cy="OBS.y" r="80" stroke="#f0a75c" stroke-width="1.3" opacity="0.9" />
            <circle :cx="OBS.x" :cy="OBS.y" r="74" stroke="#f0a75c" stroke-width="0.6" opacity="0.55" />
            <line
              v-for="(t, i) in obsTicks"
              :key="i"
              :x1="t.x1"
              :y1="t.y1"
              :x2="t.x2"
              :y2="t.y2"
              stroke="#f0a75c"
              :stroke-width="t.long ? 1.2 : 0.6"
              :opacity="t.long ? 0.9 : 0.5"
            />
          </g>
          <!-- rete — counter-rotating orbits -->
          <g id="ring-rete" class="obs-ring">
            <circle :cx="OBS.x" :cy="OBS.y" r="56" stroke="#8b6ff0" stroke-width="0.8" stroke-dasharray="2 9" opacity="0.8" />
            <ellipse :cx="OBS.x" :cy="OBS.y" rx="58" ry="26" transform-origin="1095 120" transform="rotate(24)" stroke="#8b6ff0" stroke-width="0.7" opacity="0.55" />
            <ellipse :cx="OBS.x" :cy="OBS.y" rx="58" ry="26" transform-origin="1095 120" transform="rotate(-24)" stroke="#8b6ff0" stroke-width="0.7" opacity="0.45" />
          </g>

          <!-- the eye — lids drawn, iris turning, a warm core -->
          <g id="eye-lids" stroke="#b7a3f6" stroke-width="1.6">
            <path class="t-draw" d="M1023 120 Q1095 76 1167 120" pathLength="1" stroke-dasharray="1" stroke-dashoffset="1" />
            <path class="t-draw" d="M1023 120 Q1095 164 1167 120" pathLength="1" stroke-dasharray="1" stroke-dashoffset="1" />
          </g>
          <g id="iris" class="obs-ring" stroke="#d75fe8">
            <circle :cx="OBS.x" :cy="OBS.y" r="30" stroke-width="1" opacity="0.9" />
            <circle :cx="OBS.x" :cy="OBS.y" r="23" stroke-width="0.8" stroke-dasharray="4 7" opacity="0.8" />
          </g>
          <g id="eye-core" opacity="0">
            <circle :cx="OBS.x" :cy="OBS.y" r="26" fill="url(#saga-halo)" />
            <circle :cx="OBS.x" :cy="OBS.y" r="9" stroke="#ffd9a2" stroke-width="1.4" />
            <circle :cx="OBS.x" :cy="OBS.y" r="3.4" fill="#ffe7bd" />
          </g>
          <g id="obs-waves" stroke="#8b6ff0" opacity="0">
            <circle :cx="OBS.x" :cy="OBS.y" r="96" stroke-width="0.6" class="obs-wave" />
            <circle :cx="OBS.x" :cy="OBS.y" r="118" stroke-width="0.45" class="obs-wave" style="animation-delay: -2.1s" />
          </g>
        </g>

        <!-- rays of understanding, spreading across the world -->
        <g id="rays" opacity="0" stroke="#ffd9a2" stroke-width="0.9">
          <path
            v-for="(ray, i) in RAYS"
            :key="i"
            :id="ray.id || undefined"
            class="t-draw"
            :d="ray.d"
            pathLength="1"
            stroke-dasharray="1"
            stroke-dashoffset="1"
            :opacity="0.28 + (i % 3) * 0.16"
          />
          <circle v-for="i in 3" :key="'rd' + i" class="ray-dot" :cx="OBS.x" :cy="OBS.y" r="2.1" fill="#ffe7bd" stroke="none" opacity="0" />
        </g>
      </g>

      <!-- ===================== THE WORLD-TREE (behind the left mountain) ===================== -->
      <g id="tree">
        <!-- trunk -->
        <g stroke="#b7a3f6" opacity="0.85">
          <path class="t-draw" d="M330 560 C 332 480 340 380 344 320 C 347 288 346 268 347 244" stroke-width="2.4" pathLength="1" stroke-dasharray="1" stroke-dashoffset="1" />
          <path class="t-draw" d="M356 560 C 356 490 352 396 351 330 C 350 296 349 268 347 244" stroke-width="2" pathLength="1" stroke-dasharray="1" stroke-dashoffset="1" />
          <path class="t-draw" d="M342 520 C 344 470 346 420 347 384" stroke-width="1" opacity="0.6" pathLength="1" stroke-dasharray="1" stroke-dashoffset="1" />
        </g>
        <!-- canopy -->
        <g stroke="#9d86ea" opacity="0.8">
          <path
            v-for="(p, i) in tree.paths"
            :key="i"
            class="t-draw t-limb"
            :d="p.d"
            :stroke-width="p.w"
            pathLength="1"
            stroke-dasharray="1"
            stroke-dashoffset="1"
          />
        </g>
        <g stroke="#cbbcff" stroke-width="1" opacity="0.9">
          <use v-for="(lf, i) in tree.leaves" :key="i" href="#saga-leaf" class="t-leaf" :transform="lf" opacity="0" />
        </g>
        <!-- crown sparks, lit at the very end -->
        <g id="tree-sparks" fill="#ffe7bd" opacity="0">
          <circle cx="300" cy="180" r="1.3" />
          <circle cx="392" cy="150" r="1.1" />
          <circle cx="446" cy="210" r="1.2" />
          <circle cx="262" cy="238" r="1" />
        </g>
      </g>

      <!-- ===================== THE TWO CLIFFS ===================== -->
      <g id="cliff-left">
        <path
          d="M0 900 L0 300 L40 268 L110 210 L180 240 L250 196 L320 262 L380 300 L440 356 L520 396 L578 428 L620 440 L648 470 L662 520 L648 590 L664 664 L640 742 L660 830 L640 900 Z"
          fill="#0e0b2c"
        />
        <path
          d="M0 300 L40 268 L110 210 L180 240 L250 196 L320 262 L380 300 L440 356 L520 396 L578 428 L620 440"
          stroke="#5b66a8"
          stroke-width="1.2"
          opacity="0.85"
        />
        <path d="M620 440 L648 470 L662 520 L648 590 L664 664 L640 742 L660 830 L640 900" stroke="#5b66a8" stroke-width="0.9" opacity="0.6" />
        <g stroke="#5b66a8" stroke-width="0.7" opacity="0.16">
          <path d="M150 268 L110 342" />
          <path d="M232 244 L196 330" />
          <path d="M304 300 L272 380" />
          <path d="M96 262 L66 330" />
        </g>
        <!-- a lone light calling across the gap -->
        <g id="light-left" opacity="0">
          <circle cx="598" cy="430" r="7" fill="url(#saga-halo)" class="pulse-a" />
          <circle cx="598" cy="430" r="2.2" fill="#ffd9a2" />
          <path d="M606 434 L668 452" stroke="#f0a75c" stroke-width="0.8" stroke-dasharray="3 7" opacity="0.4" />
        </g>
        <!-- life returning to the left shore -->
        <g id="flora-left" stroke="#9d86ea" stroke-width="0.9" opacity="0.75">
          <path
            v-for="([bx, by], i) in [[455, 363], [478, 375], [500, 386], [525, 399], [548, 412], [572, 425], [600, 436]]"
            :key="i"
            class="blade"
            :d="`M${bx} ${by} q ${i % 2 ? 3 : -3} -7 ${i % 2 ? 1 : -1} -13`"
          />
          <g class="blade" stroke="#d75fe8">
            <path d="M468 371 q -1 -8 -1 -12" />
            <circle cx="467" cy="357" r="1.7" />
          </g>
          <g class="blade" stroke="#f0a75c">
            <path d="M592 433 q 2 -9 1 -14" />
            <circle cx="593" cy="417" r="1.6" />
          </g>
        </g>
      </g>

      <g id="cliff-right">
        <path
          d="M1440 900 L1440 320 L1370 286 L1290 236 L1210 268 L1150 232 L1080 290 L1060 344 L980 344 L940 394 L878 424 L840 440 L812 472 L800 528 L812 598 L798 668 L818 748 L800 836 L820 900 Z"
          fill="#0e0b2c"
        />
        <path
          d="M1440 320 L1370 286 L1290 236 L1210 268 L1150 232 L1080 290 L1060 344 L980 344 L940 394 L878 424 L840 440"
          stroke="#5b66a8"
          stroke-width="1.2"
          opacity="0.85"
        />
        <path d="M840 440 L812 472 L800 528 L812 598 L798 668 L818 748 L800 836 L820 900" stroke="#5b66a8" stroke-width="0.9" opacity="0.6" />
        <g stroke="#5b66a8" stroke-width="0.7" opacity="0.16">
          <path d="M1180 290 L1148 372" />
          <path d="M1260 288 L1230 366" />
          <path d="M1332 324 L1306 398" />
        </g>
        <g id="light-right" opacity="0">
          <circle cx="844" cy="430" r="7" fill="url(#saga-halo)" class="pulse-b" />
          <circle cx="844" cy="430" r="2.2" fill="#ffd9a2" />
          <path d="M836 434 L774 452" stroke="#f0a75c" stroke-width="0.8" stroke-dasharray="3 7" opacity="0.4" />
        </g>
        <!-- the village on the shelf -->
        <g id="village" stroke="#8b6ff0" stroke-width="1" opacity="0.85">
          <path d="M988 344 v-13 l9 -9 l9 9 v13 Z M997 344 v-8" />
          <path d="M1020 344 v-12 l8 -8 l8 8 v12 Z M1028 344 v-7" />
          <path d="M1050 344 v-14 l10 -10 l10 10 v14 Z M1060 344 v-8" />
        </g>
        <g id="village-lights" fill="#ffd9a2" opacity="0">
          <circle cx="993" cy="337" r="1.2" />
          <circle cx="1032" cy="338" r="1.1" />
          <circle cx="1066" cy="336" r="1.3" />
        </g>
        <g id="flora-right" stroke="#9d86ea" stroke-width="0.9" opacity="0.75">
          <path
            v-for="([bx, by], i) in [[852, 436], [872, 428], [890, 418], [908, 408], [926, 399]]"
            :key="i"
            class="blade"
            :d="`M${bx} ${by} q ${i % 2 ? -3 : 3} -7 ${i % 2 ? -1 : 1} -12`"
          />
          <g class="blade" stroke="#d75fe8">
            <path d="M900 413 q 1 -8 1 -12" />
            <circle cx="901" cy="399" r="1.6" />
          </g>
        </g>
      </g>

      <!-- the abyss between the shores -->
      <rect id="abyss" x="540" y="450" width="400" height="450" fill="url(#saga-abyss)" opacity="0.8" />

      <!-- ===================== THE BRIDGE ===================== -->
      <g id="bridge">
        <!-- shards adrift where the span used to be.
             Wrapper g = the timeline's positional target; inner path
             carries the levitation loop — the two never fight. -->
        <g id="shards" stroke="#6f63c9" stroke-width="0.8" fill="#171236" opacity="0.9">
          <g v-for="(s, i) in shards" :key="i" class="shard" :data-fx="s.dx" :data-fy="s.dy">
            <path class="bob" :d="s.d" />
          </g>
        </g>

        <!-- the nine stones of the span -->
        <g id="stones" stroke="#6f63c9" stroke-width="1" fill="#171236">
          <g
            v-for="(s, i) in stones"
            :key="i"
            class="stone"
            :data-cx="s.cx"
            :data-cy="s.cy"
            :data-fx="s.fx"
            :data-fy="s.fy"
            :data-fr="s.fr"
            :transform="s.transform"
          >
            <path :class="s.float ? 'bob' : null" :d="s.d" />
          </g>
        </g>

        <!-- the deck, drawn beam by beam once the stones hold -->
        <path
          id="deck-glow"
          d="M600 450 Q720 384 840 450"
          stroke="url(#saga-rainbow)"
          stroke-width="13"
          stroke-linecap="round"
          filter="url(#saga-glow)"
          opacity="0"
        />
        <path
          id="deck-path"
          d="M600 450 Q720 384 840 450"
          stroke="url(#saga-rainbow)"
          stroke-width="2.2"
          stroke-linecap="round"
          pathLength="1"
          stroke-dasharray="1"
          stroke-dashoffset="1"
        />

        <!-- lanterns where the lone lights used to call -->
        <g id="lanterns" opacity="0" fill="#ffd9a2">
          <circle cx="604" cy="443" r="2" />
          <circle cx="836" cy="443" r="2" />
        </g>

        <!-- streams of light travelling the span, both ways -->
        <g id="flows" opacity="0">
          <path class="flow flow-a" d="M600 450 Q720 384 840 450" stroke="#fff3da" stroke-width="1.8" pathLength="300" stroke-dasharray="14 286" />
          <path class="flow flow-b" d="M600 450 Q720 384 840 450" stroke="#e8b3ff" stroke-width="1.4" pathLength="300" stroke-dasharray="9 291" />
        </g>

        <!-- people crossing — each one a ripple through the world -->
        <g id="travelers" opacity="0" stroke="#ffe7bd" stroke-width="1.2">
          <g v-for="i in 3" :key="i" class="traveler">
            <circle cx="0" cy="-6.5" r="1.7" fill="#ffe7bd" stroke="none" />
            <path d="M0 -5 L0 -1 M0 -1 L-1.6 2 M0 -1 L1.6 2 M-1.8 -3.4 L1.8 -3.4" />
          </g>
        </g>
        <g id="ripples" stroke="#b7a3f6" opacity="0">
          <circle id="ripple-a" cx="720" cy="440" r="6" stroke-width="0.9" />
          <circle id="ripple-b" cx="720" cy="440" r="6" stroke-width="0.7" />
        </g>
      </g>

      <!-- ===================== BIRDS ===================== -->
      <path id="bird-path" d="M-60 300 C 300 220, 800 180, 1500 260" stroke="none" />
      <g id="birds" opacity="0" stroke="#cbbcff" stroke-width="1.1">
        <g v-for="i in 3" :key="i" class="bird" :style="`--flap:${0.9 + i * 0.22}s`">
          <path class="wing" d="M-4.5 0 Q-2 -3 0 0 Q2 -3 4.5 0" />
        </g>
      </g>

      <!-- ===================== THE THREADS (the ending network) ===================== -->
      <g id="threads" opacity="0" stroke="#ffce8f" stroke-width="0.8">
        <path
          v-for="(th, i) in THREADS"
          :key="i"
          :id="th.id || undefined"
          class="t-draw"
          :d="th.d"
          pathLength="1"
          stroke-dasharray="1"
          stroke-dashoffset="1"
          :opacity="0.4 + (i % 2) * 0.2"
        />
        <circle v-for="i in 3" :key="'td' + i" class="thread-dot" cx="1095" cy="120" r="1.9" fill="#ffe7bd" stroke="none" opacity="0" />
      </g>

      <!-- ===================== FOREGROUND ===================== -->
      <g id="fg">
        <path d="M0 900 L0 780 L70 796 L150 764 L240 812 L320 790 L400 842 L470 826 L540 872 L600 900 Z" fill="#07051c" />
        <path d="M0 780 L70 796 L150 764 L240 812 L320 790 L400 842 L470 826" stroke="#464f78" stroke-width="0.9" opacity="0.4" />
        <path d="M1440 900 L1440 800 L1370 812 L1290 780 L1200 826 L1120 806 L1030 858 L960 844 L900 884 L860 900 Z" fill="#07051c" />
        <path d="M1440 800 L1370 812 L1290 780 L1200 826 L1120 806 L1030 858" stroke="#464f78" stroke-width="0.9" opacity="0.4" />
      </g>
    </g>
  </svg>
</template>

<style scoped>
.saga path,
.saga line,
.saga circle,
.saga ellipse,
.saga use {
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* parallax layers — in motion through most of the journey */
#ridge-far,
#ridge-mid,
#summit,
#tree,
#cliff-left,
#cliff-right,
#bridge,
#fg {
  will-change: transform;
}

/* rotating observatory parts, scrubbed by the parent */
.obs-ring {
  transform-box: view-box;
  transform-origin: 1095px 120px;
}

/* ambient life — cheap transform/opacity loops, world-gated by the
   parent through group opacity, all silenced under reduced motion */
.tw-a { animation: saga-twinkle 4.6s ease-in-out infinite; }
.tw-b { animation: saga-twinkle 6.4s ease-in-out -2.3s infinite; }
@keyframes saga-twinkle {
  0%, 100% { opacity: 0.9; }
  50% { opacity: 0.25; }
}

.blink-a { animation: saga-blink 3.4s ease-in-out infinite; }
.blink-b { animation: saga-blink 4.3s ease-in-out -1.2s infinite; }
.blink-c { animation: saga-blink 5.1s ease-in-out -2.6s infinite; }
@keyframes saga-blink {
  0%, 100% { opacity: 0.9; }
  45% { opacity: 0.15; }
  60% { opacity: 0.7; }
}

.pulse-a { animation: saga-pulse 2.8s ease-in-out infinite; }
.pulse-b { animation: saga-pulse 2.8s ease-in-out -1.4s infinite; }
@keyframes saga-pulse {
  0%, 100% { opacity: 0.9; }
  50% { opacity: 0.35; }
}

.aurora-drift {
  filter: blur(16px);
  animation: saga-aurora 16s ease-in-out infinite alternate;
}
.aurora-drift-b {
  filter: blur(12px);
  animation: saga-aurora 22s ease-in-out -8s infinite alternate-reverse;
}
@keyframes saga-aurora {
  from { transform: translateX(-22px); }
  to { transform: translateX(22px); }
}

.cloud-a { animation: saga-cloud 26s ease-in-out infinite alternate; }
.cloud-b { animation: saga-cloud 34s ease-in-out -12s infinite alternate-reverse; }
.cloud-c { animation: saga-cloud 30s ease-in-out -20s infinite alternate; }
@keyframes saga-cloud {
  from { transform: translateX(-46px); }
  to { transform: translateX(46px); }
}

.obs-wave {
  transform-box: view-box;
  transform-origin: 1095px 120px;
  animation: saga-wave 4.2s ease-in-out infinite;
}
@keyframes saga-wave {
  0%, 100% { transform: scale(1); opacity: 0.3; }
  50% { transform: scale(1.07); opacity: 0.12; }
}

.flow {
  animation: saga-flow 5.5s linear infinite;
}
.flow-b {
  animation-duration: 7.5s;
  animation-direction: reverse;
  animation-delay: -3s;
}
@keyframes saga-flow {
  from { stroke-dashoffset: 300; }
  to { stroke-dashoffset: 0; }
}

.bird .wing {
  transform-box: fill-box;
  transform-origin: center;
  animation: saga-flap var(--flap, 1s) ease-in-out infinite alternate;
}
@keyframes saga-flap {
  from { transform: scaleY(1); }
  to { transform: scaleY(0.35); }
}

@media (prefers-reduced-motion: reduce) {
  .tw-a, .tw-b,
  .blink-a, .blink-b, .blink-c,
  .pulse-a, .pulse-b,
  .aurora-drift, .aurora-drift-b,
  .cloud-a, .cloud-b, .cloud-c,
  .obs-wave,
  .flow,
  .bird .wing {
    animation: none;
  }
}
</style>
